import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { isoDate } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";
import { RoadSensePayload } from "../plugins/roadsense/payload";

export const ScopedLivePayloadPath = z.object({
  reportId: z.string(),
  // A plain string so an unknown site is a 404 from the handler, not a 400.
  siteId: z.string().min(1),
});
export type ScopedLivePayloadPath = z.output<typeof ScopedLivePayloadPath>;

/**
 * The workbook fields go to a tenant admin only: the stored workbook covers the
 * whole site, not the caller's movements, and the artifact route that presigns
 * it is tenant-admin only.
 */
export const ScopedRoadsenseLivePayloadResponse = z.object({
  data: z.object({
    payload: RoadSensePayload,
    /** ISO-8601 UTC instant the payload was computed at. */
    asOf: z.iso.datetime(),
    /** Tenant admin only: this site's stored `.xlsx` key, when the report has one. */
    workbookS3Key: z.string().optional(),
    /** Tenant admin only: the last local day the stored `.xlsx` covers, when the row is ready. */
    workbookPeriodEnd: isoDate.optional(),
  }),
});
export type ScopedRoadsenseLivePayloadResponse = z.output<typeof ScopedRoadsenseLivePayloadResponse>;

/**
 * GET /v2/tenants/{tenantId}/features/reports/roadsense/reports/{reportId}/sites/{siteId}/live
 *
 * One RoadSense site's live payload built from only the movements the caller
 * can view, open to any user with access in the tenant. No report, a
 * non-RoadSense report, a missing site and a site outside the caller's scope
 * are one identical 404.
 */
export const scopedRoadsenseLivePayloadResource = defineResource({
  feature: FEATURE_NAME,
  resource: "roadsense/reports/{reportId}/sites/{siteId}/live",
  resourcePathSchema: ScopedLivePayloadPath,
  scope: "tenant",
});

export const getScopedRoadsenseLivePayloadEndpoint = defineEndpoint(scopedRoadsenseLivePayloadResource, {
  responseSchema: ScopedRoadsenseLivePayloadResponse,
});
